import React, {createContext, ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {Loading, useFetchWithLoadingSmart} from "../../hooks/useFetch";
import KHExtensionsService, {KHExtension, KHExtensionType} from "../../services/KHExtensionsService";
import {KHExtensionsRuntime} from "./KHExtensionsRuntime";
import {Subject} from "rxjs";

interface KHExtensionsContextType {
    loadComponents: (wsId: string, scope: string, v: number) => Promise<React.ReactNode[]>,
    loadRaw: <T extends KHExtensionType>(wsId: string, scope: string, v: number, type: T) => Promise<KHExtension<T>[]>,
    blackboard: Blackboard,
}

interface KHExtensionsProviderProps {
    runtime: KHExtensionsRuntime,
    children: ReactNode;
}

export const KHExtensionsContext = createContext<KHExtensionsContextType | undefined>(undefined);

export const KHExtensionsContextProvider: React.FC<KHExtensionsProviderProps> = ({runtime, children}) => {
    const blackboard = useRef<Blackboard>();
    if (!blackboard.current) {
        blackboard.current = new Blackboard();
    }

    const loadComponents = useCallback(async (wsId: string, scope: string, v: number): Promise<React.ReactNode[]> => {
        const schemas = await KHExtensionsService.fetchComponents(wsId, scope, v);
        return schemas.map(x => runtime.render(x));
    }, [runtime]);

    const loadRaw = useCallback(async <T extends KHExtensionType>(wsId: string, scope: string, v: number, type: T): Promise<KHExtension<T>[]> => {
        const schemas = await KHExtensionsService.fetchPublicExtensions(wsId, scope, v, type);
        return schemas;
    }, [runtime]);

    const value = {
        loadComponents,
        loadRaw,
        blackboard: blackboard.current,
    };

    return (
        <KHExtensionsContext.Provider value={value}>
            {children}
        </KHExtensionsContext.Provider>
    );
};

export const useKHExtensionsContext = () => {
    const context = React.useContext(KHExtensionsContext);
    if (context === undefined) {
        throw new Error("useKHExtensionsContext must be used within a KHExtensionsProvider");
    }
    return context;
};

export type WorkspaceIdSource = string | (() => Promise<string>);

async function loadWorkspaceId(wsId: WorkspaceIdSource): Promise<string> {
    if (typeof wsId === "string") {
        return Promise.resolve(wsId);
    } else {
        return await wsId();
    }
}

export const useKHExtRaw = <T extends KHExtensionType>(wsId: WorkspaceIdSource, scope: string, v: number, type: T) => {
    const loader = useKHExtensionsContext();
    return useFetchWithLoadingSmart(
        () => loadWorkspaceId(wsId).then(loadedWsId => loader.loadRaw(loadedWsId, scope, v, type))
    );
};

export const useKHExtJson = (
    wsId: WorkspaceIdSource,
    scope: string,
    v: number
): Loading<KHExtension<"json">[]> => {
    return useKHExtJsonTyped(wsId, scope, v, x => x)
};

export function useKHExtJsonTyped<T extends any>(
    wsId: WorkspaceIdSource,
    scope: string,
    v: number,
    map: (x: KHExtension<"json">) => T
): Loading<T[]> {
    const loader = useKHExtensionsContext();
    return useFetchWithLoadingSmart(
        async () => {
            const loadedWsId = await loadWorkspaceId(wsId);
            const x = await loader.loadRaw(loadedWsId, scope, v, "json");
            return x.map(map);
        }
    );
}

export const useKHExtComponents = (wsId: WorkspaceIdSource, scope: string, v: number) => {
    const loader = useKHExtensionsContext();
    return useFetchWithLoadingSmart(
        () => loadWorkspaceId(wsId).then(loadedWsId => loader.loadComponents(loadedWsId, scope, v))
    );
};

export function useKHExtBlackboard() {
    const context = useKHExtensionsContext();
    return context.blackboard;
}

export function useKHExtBlackboardValue<T>(key: string): T {
    const blackboard = useKHExtBlackboard();
    const [value, setValue] = useState(
        () => blackboard.get<T>(key)
    );
    useEffect(() => {
        const sub = blackboard.changed$.subscribe(() => {
            setValue(blackboard.get<T>(key));
        });
        return sub.unsubscribe;
    }, [blackboard]);
    return value;
}

export class Blackboard {
    public readonly changed$: Subject<void> = new Subject();
    private readonly _data: Map<string, any> = new Map();

    get<T>(key: string): T {
        return this._data.get(key);
    }

    set(key: string, value: any) {
        this._data.set(key, value);
        this.changed$.next();
    }

    erase(key: string) {
        this._data.delete(key);
        this.changed$.next();
    }
}